"use client";

import { Link } from "@frontend/lib/link";
import { useEffect, useId, useRef, useState } from "react";
import { SignOutButton } from "@frontend/components/sign-out-button";
import { Avatar, cx } from "@frontend/components/ui";
import { useTheme, type ThemePreference } from "@frontend/lib/theme";
import {
  useWorkspaceSide,
  WORKSPACE_SIDE_LABELS,
  type WorkspaceSide,
} from "@frontend/lib/workspace-side";

const SIDE_OPTIONS: WorkspaceSide[] = ["creator", "brand"];

const THEME_OPTIONS: { value: ThemePreference; label: string; icon: "sun" | "moon" | "system" }[] =
  [
    { value: "light", label: "Light", icon: "sun" },
    { value: "dark", label: "Dark", icon: "moon" },
    { value: "system", label: "System", icon: "system" },
  ];

export function ProfileMenu({
  name,
  phone,
  initials,
  avatarUrl,
  pagePath = null,
  sharedAccess = false,
}: {
  name: string;
  phone: string;
  initials: string;
  avatarUrl: string | null;
  /** Own public page path (`/handle`), when claimed. */
  pagePath?: string | null;
  /** Working inside someone else’s creator account. */
  sharedAccess?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [host, setHost] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const { preference, setPreference } = useTheme();
  const { side, setSide, canToggle } = useWorkspaceSide();
  const asCollaborator = canToggle && side === "brand";

  useEffect(() => {
    setHost(window.location.host);
  }, []);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(timer);
  }, [copied]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={cx(
          "flex size-10 items-center justify-center rounded-full transition sm:h-auto sm:w-auto sm:gap-2 sm:py-1 sm:pr-2.5 sm:pl-1",
          "hover:bg-canvas focus-visible:ring-4 focus-visible:ring-brand-100 focus-visible:outline-none",
          open && "bg-canvas",
        )}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        aria-label={
          asCollaborator && sharedAccess
            ? `Account menu for ${name}, shared account, viewing as Collaborator`
            : asCollaborator
              ? `Account menu for ${name}, viewing as Collaborator`
              : sharedAccess
                ? `Account menu for ${name}, shared account`
                : `Account menu for ${name}`
        }
      >
        <span className="relative inline-flex">
          <Avatar
            name={name || initials}
            url={avatarUrl}
            size="sm"
            className={cx(
              "!size-8",
              asCollaborator && "ring-2 ring-warn-600 ring-offset-2 ring-offset-surface",
              !asCollaborator &&
                sharedAccess &&
                "ring-2 ring-money-600 ring-offset-2 ring-offset-surface",
            )}
          />
          {asCollaborator && sharedAccess ? (
            <>
              <span
                className="absolute -top-0.5 -right-0.5 flex size-3.5 items-center justify-center rounded-full bg-money-600 text-canvas shadow-sm"
                aria-hidden="true"
                title="Shared account"
              >
                <PeopleIcon className="size-2.5" />
              </span>
              <span
                className="absolute -right-0.5 -bottom-0.5 flex size-3.5 items-center justify-center rounded-full bg-warn-600 text-canvas shadow-sm"
                aria-hidden="true"
                title="Collaborator view"
              >
                <BagIcon className="size-2.5" />
              </span>
            </>
          ) : asCollaborator ? (
            <span
              className="absolute -right-0.5 -bottom-0.5 flex size-3.5 items-center justify-center rounded-full bg-warn-600 text-canvas shadow-sm"
              aria-hidden="true"
              title="Collaborator view"
            >
              <BagIcon className="size-2.5" />
            </span>
          ) : sharedAccess ? (
            <span
              className="absolute -right-0.5 -bottom-0.5 flex size-3.5 items-center justify-center rounded-full bg-money-600 text-canvas shadow-sm"
              aria-hidden="true"
              title="Shared account"
            >
              <PeopleIcon className="size-2.5" />
            </span>
          ) : null}
        </span>
        <span className="hidden max-w-[10rem] truncate text-sm font-medium text-ink sm:inline">
          {name}
        </span>
        <svg
          viewBox="0 0 20 20"
          fill="currentColor"
          className={cx("hidden size-4 text-ink-subtle transition sm:block", open && "rotate-180")}
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open ? (
        <div
          id={menuId}
          role="menu"
          aria-label="Account"
          className="absolute right-0 z-30 mt-2 w-[min(18rem,calc(100vw-1.5rem))] overflow-hidden rounded-xl border border-line-strong bg-surface py-1 shadow-[0_8px_24px_-10px_rgba(15,18,24,0.18)]"
        >
          <div className="border-b border-line/70 px-3.5 py-3">
            <div className="flex items-center gap-3">
              <Avatar name={name || initials} url={avatarUrl} className="!size-10 !text-sm" />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-ink">{name}</p>
                <p className="truncate text-xs text-ink-muted">{phone}</p>
              </div>
            </div>
          </div>

          <div className="py-1">
            <MenuLink href="/settings" onNavigate={() => setOpen(false)}>
              Settings
            </MenuLink>
            <MenuLink href="/collabs" onNavigate={() => setOpen(false)}>
              Collabs
            </MenuLink>
            <MenuLink href="/overview" onNavigate={() => setOpen(false)}>
              Overview
            </MenuLink>
          </div>

          <div className="border-t border-line/70 px-3.5 py-3">
            {pagePath ? (
              <button
                type="button"
                role="menuitem"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(`${window.location.origin}${pagePath}`);
                    setCopied(true);
                  } catch {
                    /* clipboard denied */
                  }
                }}
                title={copied ? "Copied" : "Copy link"}
                aria-label={copied ? "Copied" : "Copy link"}
                className="mb-3 flex w-full items-center gap-2 rounded-xl border border-line bg-canvas/60 py-2.5 pr-1.5 pl-3.5 text-left transition hover:bg-canvas"
              >
                <span className="min-w-0 flex-1 truncate text-xs leading-snug">
                  {host ? <span className="text-ink-subtle">{host}</span> : null}
                  <span className="font-medium text-ink">{pagePath}</span>
                </span>
                <span className="inline-flex size-8 shrink-0 items-center justify-center text-ink-muted">
                  {copied ? <CheckIcon /> : <CopyIcon />}
                </span>
              </button>
            ) : (
              <Link
                href="/settings/profile"
                role="menuitem"
                onClick={() => setOpen(false)}
                className="mb-3 flex w-full items-center justify-between gap-2.5 rounded-xl border border-line bg-canvas/60 px-3.5 py-2.5 text-left text-xs transition hover:bg-canvas"
              >
                <span className="font-medium text-ink">Claim your page link</span>
                <span className="text-ink-muted">Set up</span>
              </Link>
            )}
            {canToggle ? (
              <>
                <p className="mb-2 text-[11px] font-semibold tracking-wide text-ink-subtle uppercase">
                  Viewing as
                </p>
                <div
                  className="mb-3 grid grid-cols-2 gap-1 rounded-lg bg-canvas p-1"
                  role="group"
                  aria-label="Workspace side"
                >
                  {SIDE_OPTIONS.map((option) => {
                    const selected = side === option;
                    return (
                      <button
                        key={option}
                        type="button"
                        role="menuitemradio"
                        aria-checked={selected}
                        onClick={() => setSide(option)}
                        className={cx(
                          "rounded-md px-2 py-1.5 text-xs font-medium transition",
                          selected
                            ? "bg-surface text-ink shadow-sm ring-1 ring-line"
                            : "text-ink-muted hover:text-ink",
                        )}
                      >
                        {WORKSPACE_SIDE_LABELS[option]}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : null}

            <p className="mb-2 text-[11px] font-semibold tracking-wide text-ink-subtle uppercase">
              Appearance
            </p>
            <div
              className="grid grid-cols-3 gap-1 rounded-lg bg-canvas p-1"
              role="group"
              aria-label="Theme"
            >
              {THEME_OPTIONS.map((option) => {
                const selected = preference === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="menuitemradio"
                    aria-checked={selected}
                    onClick={() => setPreference(option.value)}
                    className={cx(
                      "inline-flex items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition",
                      selected
                        ? "bg-surface text-ink shadow-sm ring-1 ring-line"
                        : "text-ink-muted hover:text-ink",
                    )}
                  >
                    <ThemeIcon name={option.icon} />
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="border-t border-line/70 py-1">
            <SignOutButton className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-sm text-alert-600 transition hover:bg-alert-50" />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function BagIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" className={className} aria-hidden="true">
      <path
        d="M3.5 5.5h9l-.7 7.2a1.2 1.2 0 0 1-1.2 1.1H5.4a1.2 1.2 0 0 1-1.2-1.1L3.5 5.5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path
        d="M5.5 5.5V4.2a2.5 2.5 0 0 1 5 0v1.3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PeopleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" className={className} aria-hidden="true">
      <circle cx="6" cy="5.5" r="2" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M2.5 12.5c0-1.7 1.6-3 3.5-3s3.5 1.3 3.5 3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <circle cx="11" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.3" />
      <path
        d="M10 12.5c.3-1.1 1.3-2 2.6-2 .5 0 1 .1 1.4.4"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MenuLink({
  href,
  onNavigate,
  children,
}: {
  href: string;
  onNavigate: () => void;
  children: React.ReactNode;
}) {
  return (
    <Link
      href={href}
      role="menuitem"
      onClick={onNavigate}
      className="block px-3.5 py-2.5 text-sm text-ink transition hover:bg-canvas"
    >
      {children}
    </Link>
  );
}

function CopyIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="size-3.5" aria-hidden="true">
      <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M10.5 3.5a1.5 1.5 0 0 0-1.5-1.5h-5a1.5 1.5 0 0 0-1.5 1.5v5A1.5 1.5 0 0 0 3.5 10"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="size-3.5 text-money-600" aria-hidden="true">
      <path
        d="m3.5 8.5 3 3 6-6.5"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ThemeIcon({ name }: { name: "sun" | "moon" | "system" }) {
  if (name === "sun") {
    return (
      <svg viewBox="0 0 16 16" fill="none" className="size-3.5" aria-hidden="true">
        <circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M8 2v1.2M8 12.8V14M2 8h1.2M12.8 8H14M3.8 3.8l.85.85M11.35 11.35l.85.85M3.8 12.2l.85-.85M11.35 4.65l.85-.85"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (name === "moon") {
    return (
      <svg viewBox="0 0 16 16" fill="none" className="size-3.5" aria-hidden="true">
        <path
          d="M12.5 9.2A4.8 4.8 0 0 1 6.8 3.5 5.2 5.2 0 1 0 12.5 9.2Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 16 16" fill="none" className="size-3.5" aria-hidden="true">
      <rect
        x="2.5"
        y="3.5"
        width="11"
        height="9"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path d="M2.5 10.5h11" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}
